/**
 * Shared reading of a source's processing state.
 *
 * "Ready" means the whole ingestion flow finished — text extracted and
 * knowledge built — not merely that the file was uploaded. The Build action
 * depends on this, so it lives in one place rather than being re-derived.
 */

export const isSourceReady = (source) => source?.processingStatus === 'processed';

export const isSourceFailed = (source) => source?.processingStatus === 'failed';

/**
 * Still working. Deliberately strict: a source is created already marked
 * processing, so treating any other unknown status as pending would only risk
 * blocking Build forever on a row that will never change.
 */
export const isSourcePending = (source) => source?.processingStatus === 'processing';

export const countReadySources = (sources = []) => sources.filter(isSourceReady).length;

export const failedSources = (sources = []) => sources.filter(isSourceFailed);

export const pendingSources = (sources = []) => sources.filter(isSourcePending);

const plural = (count, singular) => (count === 1 ? singular : `${singular}s`);

/** The unit a source is measured in, for progress and completed counts. */
export const unitNoun = (source, count) => {
  if (source?.processingUnitsNoun === 'page') return plural(count, 'page');
  if (source?.processingUnitsNoun === 'slide') return plural(count, 'slide');
  if (source?.processingUnitsNoun === 'image') return plural(count, 'image');
  if (source?.sourceKind === 'powerpoint') return plural(count, 'slide');
  if (source?.sourceKind === 'image') return plural(count, 'image');
  if (source?.sourceKind === 'pdf' || source?.sourceKind === 'document') {
    return plural(count, 'page');
  }
  return null;
};

const FILE_FAILURE_MESSAGES = {
  UNSUPPORTED_FORMAT: "This file type can't be read yet. Try uploading it as a PDF.",
  TOO_LARGE: 'This file is too large to process. Try splitting it into smaller files.',
  UNREADABLE:
    "Couldn't read this file. The file may be corrupted, password protected, or in an unsupported format.",
  KNOWLEDGE_FAILED: "Couldn't build knowledge from this file. Try again in a moment.",
  INTERRUPTED: 'Processing was interrupted before it finished. Try again.',
};

const URL_FAILURE_MESSAGES = {
  UNSUPPORTED_FORMAT: "This page can't be read yet.",
  TOO_LARGE: 'This page is too large to process.',
  UNREADABLE:
    "Couldn't read this page. It may be unreachable, blocked, or have no readable content.",
  KNOWLEDGE_FAILED: "Couldn't build knowledge from this page. Try again in a moment.",
  INTERRUPTED: 'Processing was interrupted before it finished. Try again.',
};

export const failureMessage = (source) => {
  const messages = source?.type === 'url' ? URL_FAILURE_MESSAGES : FILE_FAILURE_MESSAGES;
  return messages[source?.processingErrorCode] || messages.UNREADABLE;
};

/**
 * Wording for the live stage. Only extraction can report real progress: the
 * knowledge step is a single call over the whole source, so it gets a label
 * rather than an invented percentage.
 */
export const progressLabel = (source) => {
  if (source?.processingStage === 'preparing') {
    return source?.type === 'url' ? 'Fetching page…' : 'Preparing document…';
  }
  if (source?.processingStage === 'knowledge') return 'Building knowledge…';

  if (source?.processingStage === 'extraction') {
    const total = source?.processingUnitsTotal;
    const done = source?.processingUnitsDone || 0;
    if (Number.isFinite(total) && total > 0) {
      const noun = unitNoun(source, total) || 'parts';
      if (source?.type === 'url' && source?.processingUnitsNoun === 'page') {
        return `Processing PDF · ${done} of ${total} ${noun}`;
      }
      return `Processing · ${done} of ${total} ${noun}`;
    }
  }
  return 'Processing…';
};

/** Percentage for the extraction stage only; null means show no bar. */
export const progressPercent = (source) => {
  if (source?.processingStage !== 'extraction') return null;
  const total = source?.processingUnitsTotal;
  if (!Number.isFinite(total) || total <= 0) return null;
  return Math.round(((source?.processingUnitsDone || 0) / total) * 100);
};
